import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { API, useLang, useCart, useSettings, fmtDzd, dzdRound, productPriceDzd, assetUrl, cldUrl } from "../lib/providers";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Trash2, Minus, Plus, ShoppingBag, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { WILAYAS } from "../lib/wilayas";
import { FreeShippingBanner } from "./Promo";
import { firePixelEvent, safeReadAttrib, getOrCreateSessionId } from "./TrackingScripts";
import ReassuranceBlock from "./ReassuranceBlock";
import Trust from "./Trust";
import { toast } from "sonner";

export default function CartSheet() {
  const { lang, t } = useLang();
  const { items, open, setOpen, remove, update, clear, totalEur } = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const payments = (settings.payment_methods || []).filter((p) => p.enabled);
  const carriers = (settings.carriers || []).filter((c) => c.enabled);
  const savedCustomer = (() => { try { return JSON.parse(localStorage.getItem("dz_customer") || "{}"); } catch { return {}; } })();
  const [form, setForm] = useState({
    name: savedCustomer.name || "", civility: "", email: savedCustomer.email || "", phone: savedCustomer.phone || "", wilaya: "", commune: "", delivery_mode: "home", address: savedCustomer.address || "",
    delivery: carriers[0]?.id || "yalidine",
    payment: payments[0]?.id || "cod",
    note: "",
  });
  // Civilité (facultatif, aucune présélection) — pastilles M./Mme/Mlle (FR) ou السيد/السيدة/الآنسة (AR)
  const CIVILITY_OPTIONS = lang === "ar"
    ? [{ value: "m", label: "السيد" }, { value: "mme", label: "السيدة" }, { value: "mlle", label: "الآنسة" }]
    : [{ value: "m", label: "M." }, { value: "mme", label: "Mme" }, { value: "mlle", label: "Mlle" }];

  // --- NAVEX / Ecotrack : chargement dynamique wilayas + communes (Phase B) ---
  // Réutilise les endpoints déjà exposés par le backend :
  //   GET /api/ecotrack/wilayas
  //   GET /api/ecotrack/wilayas/{id}/communes  → chaque commune expose {name, has_stop_desk}
  // Fallback silencieux vers la liste statique WILAYAS si l'API Ecotrack tombe.
  const [navexWilayas, setNavexWilayas] = useState([]); // [] = pas encore chargé
  const [navexCommunes, setNavexCommunes] = useState([]);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/ecotrack/wilayas`)
      .then((r) => { if (!cancelled && Array.isArray(r.data)) setNavexWilayas(r.data); })
      .catch(() => {}); // fallback silencieux vers WILAYAS statique
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!form.wilaya) { setNavexCommunes([]); return undefined; }
    let cancelled = false;
    setLoadingCommunes(true);
    axios.get(`${API}/ecotrack/wilayas/${parseInt(form.wilaya)}/communes`)
      .then((r) => { if (!cancelled && Array.isArray(r.data)) setNavexCommunes(r.data); })
      .catch(() => { if (!cancelled) setNavexCommunes([]); })
      .finally(() => { if (!cancelled) setLoadingCommunes(false); });
    return () => { cancelled = true; };
  }, [form.wilaya]);
  // Mémorise la commune sélectionnée (pour connaître has_stop_desk)
  const selectedCommune = navexCommunes.find((c) => String(c.id ?? c.name) === String(form.commune)) || null;
  const stopDeskAvailable = !!selectedCommune?.has_stop_desk;
  // Si l'utilisateur change de wilaya, on vide la commune précédente
  useEffect(() => { setForm((f) => ({ ...f, commune: "" })); }, [form.wilaya]);
  // Si la commune ne propose pas stop desk, retomber sur "home"
  useEffect(() => {
    if (form.delivery_mode === "stopdesk" && !stopDeskAvailable) {
      setForm((f) => ({ ...f, delivery_mode: "home" }));
    }
  }, [stopDeskAvailable, form.delivery_mode]);

  // NAVEX fees dynamiques : charge le tarif de la wilaya (les 2 modes en un seul appel).
  // { tarif_home_dzd, tarif_stopdesk_dzd } — 0 = fallback settings.delivery_fee_dzd
  const [navexFees, setNavexFees] = useState(null);
  useEffect(() => {
    if (!form.wilaya) { setNavexFees(null); return undefined; }
    let cancelled = false;
    axios.get(`${API}/ecotrack/fees?wilaya_id=${parseInt(form.wilaya)}`)
      .then((r) => { if (!cancelled) setNavexFees(r.data); })
      .catch(() => { if (!cancelled) setNavexFees(null); });
    return () => { cancelled = true; };
  }, [form.wilaya]);
  // Frais NAVEX effectif selon mode livraison
  const navexFeeDzd = navexFees
    ? (form.delivery_mode === "stopdesk"
        ? Number(navexFees.tarif_stopdesk_dzd || 0)
        : Number(navexFees.tarif_home_dzd || 0))
    : 0;

  const totalDzd = totalEur * (settings.exchange_rate || 280);

  // Online payment providers (Chargily, SofizPay, Guiddini)
  const [onlineProviders, setOnlineProviders] = useState([]);
  useEffect(() => {
    axios.get(`${API}/payments/providers`).then((r) => {
      setOnlineProviders((r.data?.providers || []).filter((p) => p.enabled && p.configured));
    }).catch(() => setOnlineProviders([]));
  }, []);

  // Build a single unified payment list = classic methods + online providers shown as
  // generic "Carte CIB / Edahabia" entries (provider name is hidden from the customer
  // for security; only the merchant sees the underlying gateway in the CMS).
  const onlineAsMethods = onlineProviders.map((p) => ({
    id: `online:${p.name}`,
    label_fr: "Paiement par Carte CIB / Edahabia",
    label_ar: "الدفع ببطاقة CIB / الذهبية",
    logo_url: p.logo_url || "",
    enabled: true,
    _online: true,
    _providerName: p.name,
  }));
  const allPaymentOptions = [...payments, ...onlineAsMethods];

  const [paying, setPaying] = useState(null); // provider name being processed
  const [submitting, setSubmitting] = useState(false); // anti-double-submit lock (COD/site orders)

  // Build a stable fingerprint of the current order (client+cart) to detect
  // rapid duplicate submissions (e.g. impatient double-click, page reload).
  const buildOrderFingerprint = () => {
    const itemsKey = items
      .map((i) => `${i.id}:${i._variant?.id || ""}:${i.qty}`)
      .sort()
      .join("|");
    return [
      (form.phone || "").replace(/\s+/g, ""),
      (form.email || "").toLowerCase().trim(),
      form.payment || "",
      Math.round(Number(totalDzd) || 0),
      itemsKey,
    ].join("::");
  };

  // Returns true if this exact order fingerprint was submitted in the last 45s.
  const isDuplicateWithin = (windowMs = 45000) => {
    try {
      const fp = buildOrderFingerprint();
      const raw = localStorage.getItem("last_order_fp");
      if (!raw) return false;
      const { fp: prevFp, at } = JSON.parse(raw);
      return prevFp === fp && Date.now() - at < windowMs;
    } catch { return false; }
  };
  const markSubmitted = () => {
    try {
      localStorage.setItem("last_order_fp", JSON.stringify({ fp: buildOrderFingerprint(), at: Date.now() }));
      localStorage.setItem("dz_customer", JSON.stringify({ name: form.name, phone: form.phone, email: form.email || "", address: form.address || "" }));
    } catch { /* ignore */ }
  };

  // Fire InitiateCheckout when cart is opened with items
  useEffect(() => {
    if (open && items.length > 0) {
      firePixelEvent("InitiateCheckout", {
        value: totalEur,
        currency: "EUR",
        content_ids: items.map((i) => i.id),
        content_type: "product",
        num_items: items.reduce((a, b) => a + b.qty, 0),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Upsell rules: for each rule, find category with qty >= min_qty → discount on cheapest item
  const upsells = useMemo(() => {
    const rules = settings.upsell_rules || [];
    const applied = [];
    rules.forEach((u) => {
      if (u.enabled === false) return;
      const eligible = items.filter((it) => it.category === u.category);
      const totalQty = eligible.reduce((s, it) => s + it.qty, 0);
      if (totalQty >= (u.min_qty || 2)) {
        const cheapest = [...eligible].sort((a, b) => a.price_eur - b.price_eur)[0];
        if (cheapest) {
          const discount = cheapest.price_eur * (u.discount_pct / 100);
          applied.push({ rule: u, product: cheapest, discount_eur: discount });
        }
      }
    });
    return applied;
  }, [items, settings.upsell_rules]);

  const totalDiscount = upsells.reduce((s, u) => s + u.discount_eur, 0);

  // Promo code state
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);  // {code, discount_dzd, label_fr, label_ar, valid_until}
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  const rate = settings.exchange_rate || 280;
  // Use the same DZD source as ProductCard (productPriceDzd = price_dzd or EUR×rate, rounded to 100)
  const totalDzdRaw = items.reduce((s, it) => s + productPriceDzd(it, settings) * it.qty, 0);
  const totalDzdBeforePromo = Math.max(0, totalDzdRaw - dzdRound(totalDiscount * rate));

  // --- Cadeau par palier (Priorité 2) ---
  // Le sous-total (hors livraison, après promo) déclenche le palier le plus élevé atteint.
  const promoDiscountDzdEarly = promoApplied?.discount_dzd || 0;
  const giftSubtotalDzd = Math.max(0, totalDzdBeforePromo - promoDiscountDzdEarly);
  // --- Cadeaux par palier (Priorité 2, avec cumul) ---
  // Applique la même règle que le backend (create_order) :
  //   parcours par seuil croissant,
  //   cumulative=true → s'ajoute, cumulative=false → remplace.
  const applicableGiftTiers = useMemo(() => {
    const tiers = (settings.gift_tiers || []).filter(
      (t) => t?.enabled !== false && t?.product_id && Number(t?.threshold_dzd || 0) > 0
    );
    const reached = tiers
      .filter((t) => giftSubtotalDzd >= Number(t.threshold_dzd || 0))
      .sort((a, b) => Number(a.threshold_dzd || 0) - Number(b.threshold_dzd || 0));
    let selected = [];
    for (const t of reached) {
      if (t.cumulative !== false) selected.push(t);
      else selected = [t];
    }
    // dédup par product_id
    const seen = new Set();
    return selected.filter((t) => {
      if (seen.has(t.product_id)) return false;
      seen.add(t.product_id);
      return true;
    });
  }, [settings.gift_tiers, giftSubtotalDzd]);

  // Fetch product info (name + image) for each applicable tier
  const [giftProductsMap, setGiftProductsMap] = useState({}); // { product_id: product }
  useEffect(() => {
    let cancelled = false;
    const idsToFetch = applicableGiftTiers
      .map((t) => t.product_id)
      .filter((pid) => pid && !giftProductsMap[pid]);
    if (idsToFetch.length === 0) return undefined;
    Promise.all(
      idsToFetch.map((pid) =>
        axios.get(`${API}/products/by-id/${pid}`).then((r) => [pid, r.data]).catch(() => [pid, null])
      )
    ).then((entries) => {
      if (cancelled) return;
      setGiftProductsMap((prev) => {
        const next = { ...prev };
        for (const [pid, prod] of entries) if (prod) next[pid] = prod;
        return next;
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicableGiftTiers]);


  // Save abandoned cart (debounced) when user has filled email OR phone (not yet submitted)
  useEffect(() => {
    const hasEmail = form.email && /.+@.+\..+/.test(form.email);
    const hasPhone = form.phone && form.phone.length >= 7;
    if ((!hasEmail && !hasPhone) || items.length === 0 || !open) return;
    const t = setTimeout(() => {
      const sessionId = getOrCreateSessionId();
      const orderItems = items.map((it) => ({
        product_id: it.id, name: it.name, qty: it.qty, unit_price_eur: it.price_eur,
        variant_id: it._variant?.id || null, options: it._selected_options || {},
      }));
      axios.post(`${API}/carts/abandoned`, {
        session_id: sessionId,
        customer_name: form.name || "",
        customer_email: form.email || "",
        customer_phone: form.phone || "",
        customer_lang: lang,
        wilaya: form.wilaya ? parseInt(form.wilaya) || null : null,
        items: orderItems,
        total_eur: totalEur,
        total_dzd: totalDzdBeforePromo,
      }).catch(() => {}); // silent fail (non-blocking)
    }, 1500);
    return () => clearTimeout(t);
  }, [form.name, form.email, form.phone, form.wilaya, items, totalEur, totalDzdBeforePromo, open, lang]);
  const promoDiscountDzd = promoApplied?.discount_dzd || 0;
  // Delivery fee: tarif NAVEX temps réel (par wilaya, par mode) + 100 DA (stockage/manutention).
  // Aucun arrondi appliqué. Si NAVEX indispo, fallback settings.delivery_fee_dzd / delivery_fee_stopdesk_dzd.
  const NAVEX_STORAGE_SURCHARGE_DZD = 100;
  const homeCostRawDzd = navexFees && Number(navexFees.tarif_home_dzd) > 0
    ? Number(navexFees.tarif_home_dzd)
    : Number(settings.delivery_fee_dzd ?? 500);
  const stopdeskCostRawDzd = navexFees && Number(navexFees.tarif_stopdesk_dzd) > 0
    ? Number(navexFees.tarif_stopdesk_dzd)
    : Number(settings.delivery_fee_stopdesk_dzd ?? 250);
  const homeFeeBase = homeCostRawDzd + NAVEX_STORAGE_SURCHARGE_DZD;
  const stopdeskFeeBase = stopdeskCostRawDzd + NAVEX_STORAGE_SURCHARGE_DZD;
  // Coût réel NAVEX (sans notre marge stockage/manutention) pour le mode sélectionné —
  // transmis au backend tel quel (delivery_cost_dzd_client), jamais confondu avec le prix client.
  const deliveryCostRawDzd = form.delivery_mode === "stopdesk" ? stopdeskCostRawDzd : homeCostRawDzd;

  // Paliers de livraison séparés (Stop Desk / Domicile) — réutilisent la même base que
  // les cadeaux par palier (giftSubtotalDzd = sous-total après promo).
  const freeThrStopdesk = Number(settings.free_shipping_threshold_stopdesk_dzd ?? 10000);
  const freeThrHome = Number(settings.free_shipping_threshold_home_dzd ?? 20000);
  const subtotalForShipping = totalDzdBeforePromo - promoDiscountDzd;
  const stopdeskAdvantageReached = freeThrStopdesk > 0 && subtotalForShipping >= freeThrStopdesk;
  const homeAdvantageReached = freeThrHome > 0 && subtotalForShipping >= freeThrHome;
  // Raw delivery fee (before pickup-mode override) : Stop Desk gratuit dès son seuil ; Domicile
  // gratuit dès son seuil, ou bénéficie de l'avantage Stop Desk déjà acquis en réduction partielle.
  let deliveryDzdRaw;
  if (form.delivery_mode === "stopdesk") {
    deliveryDzdRaw = stopdeskAdvantageReached ? 0 : stopdeskFeeBase;
  } else if (homeAdvantageReached) {
    deliveryDzdRaw = 0;
  } else if (stopdeskAdvantageReached) {
    deliveryDzdRaw = Math.max(0, homeFeeBase - stopdeskFeeBase);
  } else {
    deliveryDzdRaw = homeFeeBase;
  }

  // Detect "pickup in store" payment methods (the admin can name them however
  // they want — match on id and on FR/AR label keywords).
  const isPickupMethod = (p) => {
    if (!p) return false;
    const id = (p.id || "").toLowerCase();
    const fr = (p.label_fr || "").toLowerCase();
    const ar = p.label_ar || "";
    return /pickup|retrait|magasin|store/.test(id)
      || /retrait/.test(fr) || /magasin/.test(fr) || /sur place/.test(fr)
      || ar.includes("استلام") || ar.includes("المتجر");
  };
  const selectedPayment = (settings.payment_methods || []).find((p) => p.id === form.payment);
  const isPickup = isPickupMethod(selectedPayment);

  // Pickup orders never pay shipping
  // Delivery selection completeness — the shipping amount (and free-shipping
  // discount) must NOT be shown before the customer has picked wilaya + commune
  // + delivery mode. Pickup-in-store bypasses this rule.
  const deliverySelectionComplete = !!(form.wilaya && form.commune && form.delivery_mode);
  const showDeliveryAmount = isPickup || deliverySelectionComplete;
  const deliveryDzd = isPickup ? 0 : (showDeliveryAmount ? deliveryDzdRaw : 0);
  const finalEur = Math.max(0, totalEur - totalDiscount - (promoDiscountDzd / rate)) + (deliveryDzd / rate);
  const finalDzd = Math.max(0, totalDzdBeforePromo - promoDiscountDzd) + deliveryDzd;

  const applyPromo = async () => {
    const code = (promoInput || "").trim().toUpperCase();
    if (!code) return;
    setPromoError(""); setPromoLoading(true);
    try {
      const { data } = await axios.post(`${API}/promo-codes/check`, {
        code, phone: form.phone, cart_total_dzd: totalDzdBeforePromo,
      });
      setPromoApplied(data);
    } catch (e) {
      setPromoError(e?.response?.data?.detail || "Code invalide");
      setPromoApplied(null);
    } finally { setPromoLoading(false); }
  };

  const removePromo = () => { setPromoApplied(null); setPromoInput(""); setPromoError(""); };

  const buildMessage = () => {
    const wilaya = WILAYAS.find((w) => String(w.n) === String(form.wilaya));
    const wilayaLabel = wilaya ? `${wilaya.n} - ${lang === "ar" ? wilaya.ar : wilaya.fr}` : form.wilaya;
    const carrier = carriers.find((c) => c.id === form.delivery);
    const pm = payments.find((p) => p.id === form.payment);
    const pmLabel = pm ? (lang === "ar" ? pm.label_ar : pm.label_fr) : form.payment;
    const header = lang === "ar" ? (settings.wa_message_template_ar || "طلب جديد") : (settings.wa_message_template_fr || "NOUVELLE COMMANDE");
    const itemsBlock = items.map((p) => {
      // Use human-friendly labels from product.options[] when available
      const optsPairs = Object.entries(p._selected_options || {});
      const optsLabel = optsPairs.length
        ? " (" + optsPairs.map(([k, v]) => {
            const def = (p.options || []).find((o) => o.id === k);
            const label = def ? (lang === "ar" ? (def.label_ar || def.label_fr) : def.label_fr) : k;
            return `${label}: ${v}`;
          }).join(", ") + ")"
        : "";
      return `• ${p.name}${optsLabel}  ×${p.qty}  →  ${fmtDzd(productPriceDzd(p, settings) * p.qty)}`;
    }).join("\n");
    const upsellsBlock = upsells.length > 0
      ? (lang === "ar" ? "*🎁 خصومات تلقائية :*\n" : "*🎁 Remises auto :*\n") +
        upsells.map((u) => `🎁 ${(lang === "ar" ? u.rule.label_ar : u.rule.label_fr) || `-${u.rule.discount_pct}%`} → -${fmtDzd(u.discount_eur * rate)}`).join("\n") + "\n\n"
      : "";
    const promoBlock = promoApplied
      ? (lang === "ar" ? `*🎟️ كود الخصم ${promoApplied.code} :* -${fmtDzd(promoApplied.discount_dzd)}\n\n` : `*🎟️ Code promo ${promoApplied.code} :* -${fmtDzd(promoApplied.discount_dzd)}\n\n`)
      : "";
    const deliveryLabel = isPickup
      ? (lang === "ar" ? "🏪 استلام من المتجر" : "🏪 Retrait magasin")
      : (deliveryDzd === 0 ? (lang === "ar" ? "مجاني 🎁" : "OFFERTE 🎁") : `${fmtDzd(deliveryDzd)}${carrier?.name ? ` (${carrier.name})` : ""}`);
    const carrierLabel = isPickup ? (lang === "ar" ? "استلام من المتجر" : "Retrait magasin") : (carrier?.name || form.delivery || "—");
    const noteLine = form.note ? (lang === "ar" ? `📝 ملاحظة : ${form.note}\n` : `📝 Note : ${form.note}\n`) : "";
    const rawTemplate = (lang === "ar"
      ? (settings.wa_full_template_ar || settings.wa_full_template_fr)
      : settings.wa_full_template_fr) || "";
    // If no full template is configured (very old settings), fall back to the legacy hard-coded layout.
    if (!rawTemplate.includes("{items}")) {
      return encodeURIComponent(
        `*${header}*\n\n*🛒 Produits :*\n${itemsBlock}\n\n${upsellsBlock}${promoBlock}` +
        `*💰 Sous-total :* ${fmtDzd(totalDzdBeforePromo - promoDiscountDzd)}\n` +
        `*🚚 Livraison :* ${deliveryLabel}\n*✅ TOTAL :* ${fmtDzd(finalDzd)}\n\n` +
        `*👤 Client :*\nNom : ${form.name}\nTéléphone : ${form.phone}\nWilaya : ${wilayaLabel}\n` +
        (isPickup ? "" : `Adresse : ${form.address}\n`) +
        `Transporteur : ${carrierLabel}\nPaiement : ${pmLabel}\n${noteLine}`
      );
    }
    const replacements = {
      "{shop_name}": settings.site_name || "DZ Tennis Shop",
      "{header}": header,
      "{name}": form.name || "—",
      "{phone}": form.phone || "—",
      "{email}": form.email || "",
      "{wilaya}": wilayaLabel || "—",
      "{address}": isPickup ? (lang === "ar" ? "استلام من المتجر" : "Retrait magasin") : (form.address || "—"),
      "{commune}": form.commune || "",
      "{carrier}": carrierLabel,
      "{payment}": pmLabel || "—",
      "{note}": noteLine,
      "{items}": itemsBlock || "—",
      "{upsells}": upsellsBlock,
      "{promo}": promoBlock,
      "{subtotal}": fmtDzd(totalDzdBeforePromo - promoDiscountDzd),
      "{delivery}": deliveryLabel,
      "{total}": fmtDzd(finalDzd),
      "{order_ref}": form.order_ref || "",
    };
    let msg = rawTemplate;
    for (const [k, v] of Object.entries(replacements)) {
      msg = msg.split(k).join(String(v));
    }
    return encodeURIComponent(msg);
  };

  // Nom de la commune sélectionnée (envoyé au backend + affiché sur WhatsApp/facture)
  const communeName = selectedCommune?.name || "";
  // delivery_type envoyé au backend : pickup magasin > stopdesk > home
  const deliveryType = isPickup ? "pickup" : (form.delivery_mode === "stopdesk" ? "stopdesk" : "home");
  // En stop-desk on n'exige plus d'adresse. En pickup l'adresse est optionnelle.
  // Validation téléphone algérien : local `0[5-7]XXXXXXXX` (10 chiffres)
  // ou international `+213[5-7]XXXXXXXX`. NAVEX rejette tout numéro étranger.
  const dzPhoneRegex = /^(?:\+?213|0)([567])\d{8}$/;
  const phoneDigits = (form.phone || "").replace(/[\s.\-()]/g, "");
  const phoneValid = dzPhoneRegex.test(phoneDigits);
  const phoneError = form.phone && !phoneValid
    ? (lang === "ar"
        ? "رقم غير صالح — يجب أن يكون جزائريًا (0X أو +213)"
        : "Numéro invalide — doit être algérien (0X ou +213…)")
    : "";
  const canSubmit = items.length > 0 && form.name && form.phone && phoneValid && form.wilaya
    && (isPickup || form.commune)
    && (isPickup || form.delivery_mode === "stopdesk" || form.address);
  // Champs manquants affichés sous le bouton "Commander" (sinon le bouton grisé n'explique rien)
  const missingFields = [];
  if (!form.name) missingFields.push(lang === "ar" ? "الاسم" : "nom");
  if (!form.phone || !phoneValid) missingFields.push(lang === "ar" ? "رقم هاتف صحيح" : "téléphone valide");
  if (!form.wilaya) missingFields.push(lang === "ar" ? "الولاية" : "wilaya");
  if (!isPickup && form.wilaya && !form.commune) missingFields.push(lang === "ar" ? "البلدية" : "commune");
  if (!isPickup && form.delivery_mode !== "stopdesk" && !form.address) missingFields.push(lang === "ar" ? "العنوان" : "adresse");

  const submit = async () => {
    if (!canSubmit || submitting || paying) return;
    if (isDuplicateWithin()) {
      alert(lang === "ar"
        ? "لقد أرسلت هذا الطلب للتو. يرجى الانتظار قبل إعادة المحاولة."
        : "Cette commande vient d'être envoyée. Merci de patienter avant de réessayer.");
      return;
    }
    setSubmitting(true);
    let createdOrderId = null;
    try {
      const orderItems = items.map((it) => ({
        product_id: it.id,
        name: it.name,
        qty: it.qty,
        unit_price_eur: it.price_eur,
        variant_id: it._variant?.id || null,
        options: it._selected_options || {},
      }));
      const { data: createdOrder } = await axios.post(`${API}/orders`, {
        items: orderItems,
        customer: { name: form.name, civility: form.civility || "", phone: form.phone, email: form.email || "", wilaya: parseInt(form.wilaya) || null, address: form.address, commune: communeName || form.address },
        delivery_carrier: isPickup ? "" : (navexFeeDzd > 0 ? "NAVEX Delivery" : form.delivery),
        delivery_type: deliveryType,
        delivery_fee_dzd_client: deliveryDzd,
        delivery_cost_dzd_client: isPickup ? 0 : deliveryCostRawDzd,
        payment_method: form.payment, note: form.note, channel: "site",
        promo_code: promoApplied?.code || null,
        discount_eur: promoApplied ? (promoApplied.discount_dzd / rate) : 0,
        customer_lang: lang,
        ...safeReadAttrib(),
        session_id: getOrCreateSessionId(),
      });
      createdOrderId = createdOrder?.id;
      markSubmitted();
      // Fire Purchase event client-side (dedup with server-side CAPI via event_id = order.id)
      firePixelEvent("Purchase", {
        value: totalEur,
        currency: "EUR",
        content_ids: items.map((i) => i.id),
        content_type: "product",
        num_items: items.reduce((a, b) => a + b.qty, 0),
        order_id: createdOrderId,
      });
    } catch (e) {
      console.error("order persist fail", e);
    } finally {
      setSubmitting(false);
    }
    if (!createdOrderId) return;
    clear();
    setOpen(false);
    // Redirect to thank-you or virement page (no more direct WhatsApp popup — customer sees confirmation first)
    const isTransfer = ["transfer","virement","rib","ccp","bank_transfer"].includes((form.payment || "").toLowerCase());
    navigate(isTransfer ? `/virement/${createdOrderId}` : `/merci/${createdOrderId}`);
  };

  const submitOnline = async (providerName) => {
    if (!canSubmit || paying || submitting) return;
    if (isDuplicateWithin()) {
      alert(lang === "ar"
        ? "لقد أرسلت هذا الطلب للتو. يرجى الانتظار قبل إعادة المحاولة."
        : "Cette commande vient d'être envoyée. Merci de patienter avant de réessayer.");
      return;
    }
    setPaying(providerName);
    try {
      // 1. Create order on backend (same payload as submit)
      const orderItems = items.map((it) => ({
        product_id: it.id,
        name: it.name,
        qty: it.qty,
        unit_price_eur: it.price_eur,
        variant_id: it._variant?.id || null,
        options: it._selected_options || {},
      }));
      const { data: createdOrder } = await axios.post(`${API}/orders`, {
        items: orderItems,
        customer: { name: form.name, civility: form.civility || "", phone: form.phone, email: form.email || "", wilaya: parseInt(form.wilaya) || null, address: form.address, commune: communeName || form.address },
        delivery_carrier: navexFeeDzd > 0 ? "NAVEX Delivery" : form.delivery,
        delivery_type: deliveryType,
        delivery_fee_dzd_client: deliveryDzd,
        delivery_cost_dzd_client: isPickup ? 0 : deliveryCostRawDzd,
        payment_method: providerName, note: form.note, channel: "site",
        promo_code: promoApplied?.code || null,
        discount_eur: promoApplied ? (promoApplied.discount_dzd / rate) : 0,
        customer_lang: lang,
        ...safeReadAttrib(),
        session_id: getOrCreateSessionId(),
      });
      markSubmitted();
      // 2. Get checkout URL from provider
      const { data: checkout } = await axios.post(`${API}/payments/checkout`, {
        order_id: createdOrder.id,
        provider: providerName,
      });
      if (!checkout?.checkout_url) throw new Error("No checkout URL");
      // 3. Redirect customer to payment page
      window.location.href = checkout.checkout_url;
    } catch (e) {
      console.error("online checkout failed", e);
      const msg = e?.response?.data?.detail || e.message || "Erreur paiement";
      alert(lang === "ar" ? `خطأ في الدفع: ${msg}` : `Erreur de paiement : ${msg}`);
      setPaying(null);
    }
  };

  const codMaxRaw = Number(settings.cod_max_dzd ?? 10000);
  // codMaxRaw <= 0 means COD is UNLIMITED (available at any amount)
  const codUnlimited = codMaxRaw <= 0;
  const codMax = codUnlimited ? Infinity : codMaxRaw;
  const codDisabled = !codUnlimited && totalDzd > codMax;
  const visiblePayments = payments.filter((p) => {
    // hide COD (cash on delivery) when over threshold
    const isCod = ["cod", "cash", "cash_on_delivery", "espece"].includes((p.id || "").toLowerCase())
      || (p.label_fr || "").toLowerCase().includes("livraison");
    if (isCod && codDisabled) return false;
    return true;
  });
  // If currently-selected payment got hidden, reset selection
  useEffect(() => {
    if (form.payment && !visiblePayments.find((p) => p.id === form.payment)) {
      setForm((f) => ({ ...f, payment: visiblePayments[0]?.id || "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codDisabled]);

  // When user picks the "Pickup in store" payment, clear the delivery carrier
  // (it would be misleading to ship a pickup order). We restore the default
  // carrier when they switch back to a delivery payment method.
  useEffect(() => {
    if (isPickup && form.delivery) {
      setForm((f) => ({ ...f, delivery: "" }));
    } else if (!isPickup && !form.delivery && carriers.length > 0) {
      setForm((f) => ({ ...f, delivery: carriers[0].id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPickup]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side={lang === "ar" ? "left" : "right"}
        className="bg-[var(--c-bg)] border-white/10 text-white w-full sm:max-w-lg overflow-y-auto"
        data-testid="cart-sheet"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle className="font-heading uppercase tracking-tight text-2xl text-white flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-neon" />{t.cart.title}
          </SheetTitle>
          <SheetDescription className="text-white/50 text-xs">
            {lang === "ar" ? "أكمل طلبك في ثوانٍ" : "Finalisez votre commande en quelques secondes"}
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="text-center py-16 text-white/40" data-testid="cart-empty">{t.cart.empty}</div>
        ) : (
          <div className="mt-6 space-y-3" data-testid="cart-items">
            {/* Barre de progression des cadeaux par palier (mêmes paliers / même sous-total que le calcul des cadeaux) */}
            {(() => {
              const allTiers = (settings.gift_tiers || [])
                .filter((x) => x?.enabled !== false && x?.product_id && Number(x?.threshold_dzd || 0) > 0)
                .sort((a, b) => Number(a.threshold_dzd) - Number(b.threshold_dzd));
              if (allTiers.length === 0) return null;
              const top = Number(allTiers[allTiers.length - 1].threshold_dzd);
              const nextTier = allTiers.find((x) => giftSubtotalDzd < Number(x.threshold_dzd));
              const pct = Math.min(100, (giftSubtotalDzd / top) * 100);
              const tierLabel = (x) => (lang === "ar" ? (x.label_ar || x.label_fr) : x.label_fr) || "";
              return (
                <div className="border border-white/10 bg-white/[0.03] p-4 pb-9" data-testid="cart-gift-progress">
                  <p className="text-sm leading-snug">
                    {nextTier ? (
                      lang === "ar"
                        ? <>بقي <b className="text-neon">{fmtDzd(Number(nextTier.threshold_dzd) - giftSubtotalDzd)}</b> للحصول على: <b className="text-neon">{tierLabel(nextTier)}</b></>
                        : <>Plus que <b className="text-neon">{fmtDzd(Number(nextTier.threshold_dzd) - giftSubtotalDzd)}</b> pour recevoir : <b className="text-neon">{tierLabel(nextTier)}</b></>
                    ) : (
                      <b className="text-neon">🎁 {lang === "ar" ? "تم فتح كل الهدايا!" : "Tous vos cadeaux sont débloqués !"}</b>
                    )}
                  </p>
                  <div className="relative h-1.5 bg-white/15 rounded-full mt-5 mx-2">
                    <div className="absolute inset-y-0 start-0 bg-neon rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    {allTiers.map((x) => {
                      const reached = giftSubtotalDzd >= Number(x.threshold_dzd);
                      return (
                        <span key={x.id || x.product_id} className="absolute top-1/2" style={{ insetInlineStart: `${(Number(x.threshold_dzd) / top) * 100}%` }}>
                          <span className={`block w-3 h-3 -mt-1.5 -ms-1.5 rounded-full border-2 ${reached ? "bg-neon border-neon" : "bg-[#333] border-white/40"}`} />
                          <span className={`absolute top-3 -translate-x-1/2 rtl:translate-x-1/2 whitespace-nowrap text-xs ${reached ? "text-neon" : "text-white/60"}`}>{fmtDzd(x.threshold_dzd)}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            {items.map((it) => {
              // Build human-readable options label using product.options[].label_fr
              // and swap image if the selected variant has its own image_url.
              const selOpts = it._selected_options || {};
              const optsPairs = Object.entries(selOpts);
              const variantImg = it._variant?.image_url || "";
              const displayImg = variantImg || it.image_url;
              return (
              <div key={it.cart_key || it.id} className="flex gap-3 border border-white/10 p-3">
                {displayImg && <img src={cldUrl(displayImg, { w: 160 })} alt={it.name} className="w-16 h-16 object-cover" loading="lazy" decoding="async" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm uppercase truncate">{it.name}</div>
                  {optsPairs.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-1" data-testid={`cart-variant-${it.id}`}>
                      {optsPairs.map(([k, v]) => {
                        const optDef = (it.options || []).find((o) => o.id === k);
                        const label = optDef ? (lang === "ar" ? (optDef.label_ar || optDef.label_fr) : optDef.label_fr) : k;
                        return (
                          <span key={k} className="text-[10px] uppercase tracking-widest border border-neon/40 text-neon/90 px-1.5 py-0.5">
                            {label}: <span className="text-white font-bold">{v}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className="text-xs text-neon font-black mt-1">{fmtDzd(productPriceDzd(it, settings) * it.qty)}</div>
                  {(() => {
                    const max = it.variant_stock_count != null ? it.variant_stock_count : it.stock_count;
                    const atMax = max != null && it.qty >= max;
                    return (
                      <>
                        <div className="flex items-center gap-2 mt-2">
                          <button onClick={() => update(it.cart_key || it.id, it.qty - 1)} className="w-9 h-9 border border-white/20 hover:border-neon flex items-center justify-center" data-testid={`qty-minus-${it.id}`}><Minus className="w-3.5 h-3.5" /></button>
                          <span className="text-sm font-bold w-8 text-center">{it.qty}</span>
                          <button
                            onClick={() => {
                              if (atMax) { toast.error(`Stock max atteint (${max} dispo)`); return; }
                              update(it.cart_key || it.id, it.qty + 1);
                            }}
                            disabled={atMax}
                            className={`w-9 h-9 border flex items-center justify-center ${atMax ? "border-white/10 text-white/30 cursor-not-allowed" : "border-white/20 hover:border-neon"}`}
                            data-testid={`qty-plus-${it.id}`}
                            title={atMax ? `Stock max : ${max}` : "Augmenter"}
                          ><Plus className="w-3.5 h-3.5" /></button>
                          <button onClick={() => remove(it.cart_key || it.id)} className="ms-auto text-clay" data-testid={`remove-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                        {max != null && max <= 5 && (
                          <div className={`text-[10px] mt-1 ${atMax ? "text-red-400" : "text-yellow-300/80"}`}>
                            {atMax ? `⛔ Stock max atteint (${max})` : `⚠️ Plus que ${max} en stock`}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              );
            })}

            {/* Cadeaux par palier — non retirables, prix 0 (avec cumul) */}
            {applicableGiftTiers.map((tier) => {
              const gp = giftProductsMap[tier.product_id];
              if (!gp) return null;
              return (
                <div
                  key={tier.id || tier.product_id}
                  className="flex gap-3 border-2 border-neon/60 bg-neon/5 p-3"
                  data-testid={`cart-gift-${tier.id || tier.product_id}`}
                >
                  {gp.image_url && (
                    <img
                      src={cldUrl(gp.image_url, { w: 160 })}
                      alt={gp.name}
                      className="w-16 h-16 object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-neon font-black flex items-center gap-1">
                      🎁 {lang === "ar"
                        ? (tier.label_ar || "هدية مجانية")
                        : (tier.label_fr || "Cadeau offert")}
                    </div>
                    <div className="font-bold text-sm uppercase truncate">{gp.name}</div>
                    <div className="text-xs text-neon font-black mt-1">
                      {lang === "ar" ? "مجاناً" : "OFFERT"}
                    </div>
                    <div className="text-[10px] text-white/50 mt-0.5">
                      {lang === "ar"
                        ? `دبر شراء ابتداء من ${fmtDzd(tier.threshold_dzd)}`
                        : `Débloqué dès ${fmtDzd(tier.threshold_dzd)} d'achat`}
                    </div>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-full border border-dashed border-neon/50 text-neon py-3 text-sm font-bold hover:bg-neon/10 transition-colors"
              data-testid="cart-continue-shopping"
            >
              + {lang === "ar" ? "إضافة منتجات أخرى" : "Ajouter d'autres produits"}
            </button>

            <div className="border-t border-white/10 pt-4 space-y-1">
              {/* Promo code input */}
              {settings.promo_code_field_in_cart !== false && items.length > 0 && (
                <div className="mb-3" data-testid="cart-promo-block">
                  {promoApplied ? (
                    <div className="flex items-center justify-between border border-neon/30 bg-neon/5 p-2 text-xs">
                      <span className="text-neon font-bold">🎟️ {promoApplied.code} — {promoApplied.label_fr || `-${fmtDzd(promoApplied.discount_dzd)}`}</span>
                      <button onClick={removePromo} className="text-clay hover:opacity-80 text-[10px] uppercase tracking-widest" data-testid="remove-promo">Retirer</button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <Input
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value)}
                        placeholder={lang === "ar" ? "كود الخصم" : "Code promo"}
                        className="bg-black/40 border-white/10 rounded-none h-9 text-sm uppercase"
                        data-testid="promo-input"
                        onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                      />
                      <Button onClick={applyPromo} disabled={promoLoading || !promoInput.trim()} className="bg-neon text-black hover:opacity-90 rounded-none h-9 text-xs font-black uppercase tracking-widest px-3" data-testid="apply-promo">
                        {promoLoading ? "…" : (lang === "ar" ? "تطبيق" : "Appliquer")}
                      </Button>
                    </div>
                  )}
                  {promoError && <p className="text-[10px] text-clay mt-1" data-testid="promo-error">{promoError}</p>}
                </div>
              )}
              <div className="flex justify-between text-sm"><span className="text-white/60">{lang === "ar" ? "المجموع الفرعي" : "Sous-total"}</span><span className="text-white/90 font-medium" data-testid="subtotal-dzd">{fmtDzd(totalDzdBeforePromo - promoDiscountDzd)}</span></div>
              {!isPickup && (
                <div className="flex justify-between text-sm" data-testid="delivery-line">
                  <span className="text-white/60">
                    {lang === "ar" ? "التوصيل" : "Livraison"}
                    {(() => {
                      const c = carriers.find((x) => x.id === form.delivery);
                      return c ? <span className="text-white/40"> · {c.name}</span> : null;
                    })()}
                  </span>
                  {!showDeliveryAmount ? (
                    <span className="text-white/60 italic text-xs" data-testid="delivery-value">
                      {lang === "ar" ? "اختر الولاية والبلدية" : "Choisissez wilaya + commune"}
                    </span>
                  ) : deliveryDzd === 0 ? (
                    <span className="text-neon font-bold uppercase text-xs" data-testid="delivery-value">
                      {lang === "ar" ? "مجاني" : "Gratuite"}
                    </span>
                  ) : (
                    <span className="text-white/90 font-medium flex items-center gap-1.5" data-testid="delivery-value">
                      {fmtDzd(deliveryDzd)}
                      {navexFeeDzd > 0 && !isPickup && (
                        <span className="text-[9px] uppercase tracking-widest text-neon border border-neon/40 px-1 py-0.5" title="Tarif NAVEX temps réel">
                          NAVEX
                        </span>
                      )}
                    </span>
                  )}
                </div>
              )}
              <div className="flex justify-between text-sm border-t border-white/10 pt-2"><span className="text-white/70 uppercase text-xs tracking-widest">{t.cart.total_dzd}</span><span className="font-heading font-black text-neon text-lg" data-testid="total-dzd">{fmtDzd(finalDzd)}</span></div>
              {upsells.length > 0 && (
                <div className="border-t border-neon/30 pt-2 mt-1" data-testid="upsell-bar">
                  {upsells.map((u, i) => (
                    <div key={i} className="flex justify-between text-[11px] text-neon">
                      <span>🎁 {(lang === "ar" ? u.rule.label_ar : u.rule.label_fr) || `-${u.rule.discount_pct}%`}</span>
                      <span className="font-bold">-{fmtDzd(u.discount_eur * settings.exchange_rate)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <FreeShippingBanner subtotalDzd={subtotalForShipping} />

            <div className="border-t border-white/10 pt-5 mt-5 space-y-4">
              <h3 className="font-heading font-bold uppercase tracking-wide text-sm">{t.form.title}</h3>
              <Field label={t.form.name}>
                <div className="flex gap-1.5 mb-1.5" data-testid="civility-pills">
                  {CIVILITY_OPTIONS.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      data-testid={`civility-pill-${c.value}`}
                      onClick={() => setForm({ ...form, civility: form.civility === c.value ? "" : c.value })}
                      className={`px-2.5 py-1 text-[11px] uppercase tracking-wide border rounded-full transition-colors ${form.civility === c.value ? "bg-neon text-black border-neon" : "border-white/20 text-white/60 hover:border-white/40"}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" autoComplete="name" data-testid="form-name" />
              </Field>
              <Field label={t.form.phone}>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} type="tel" inputMode="tel" autoComplete="tel" placeholder="0555 12 34 56" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="form-phone" />
                {phoneError && (
                  <p className="text-xs text-rose-400 mt-1" data-testid="phone-error">{phoneError}</p>
                )}
              </Field>
              <Field label={t.form.wilaya}>
                <Select value={form.wilaya} onValueChange={(v) => setForm({ ...form, wilaya: v, commune: "" })}>
                  <SelectTrigger className="bg-black/40 border-white/10 rounded-none" data-testid="form-wilaya"><SelectValue placeholder={t.form.wilaya_placeholder} /></SelectTrigger>
                  <SelectContent className="bg-[var(--c-bg)] border-white/10 text-white max-h-72">
                    {(navexWilayas.length > 0 ? navexWilayas : WILAYAS.map((w) => ({ id: w.n, name: lang === "ar" ? w.ar : w.fr })))
                      .map((w) => (
                        <SelectItem key={w.id} value={String(w.id)} className="focus:bg-neon focus:text-black">
                          {String(w.id).padStart(2, "0")} — {w.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </Field>

              {/* NAVEX Commune — visible dès qu'une wilaya est choisie et que les communes ont chargé */}
              {form.wilaya && !isPickup && (
                <Field label={lang === "ar" ? "البلدية" : "Commune"}>
                  <Select
                    value={form.commune}
                    onValueChange={(v) => setForm({ ...form, commune: v })}
                    disabled={loadingCommunes || navexCommunes.length === 0}
                  >
                    <SelectTrigger className="bg-black/40 border-white/10 rounded-none" data-testid="form-commune">
                      <SelectValue placeholder={
                        loadingCommunes
                          ? (lang === "ar" ? "جارٍ التحميل…" : "Chargement…")
                          : navexCommunes.length === 0
                          ? (lang === "ar" ? "لا توجد بلديات متاحة" : "Aucune commune disponible")
                          : (lang === "ar" ? "اختر بلدية" : "Sélectionner une commune")
                      } />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--c-bg)] border-white/10 text-white max-h-72">
                      {navexCommunes.map((c) => (
                        <SelectItem key={c.id ?? c.name} value={String(c.id ?? c.name)} className="focus:bg-neon focus:text-black">
                          {c.name}{c.has_stop_desk ? "  🏢" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              {/* Mode de livraison NAVEX — Domicile / Stop Desk */}
              {form.wilaya && form.commune && !isPickup && (
                <Field label={lang === "ar" ? "طريقة التوصيل" : "Mode de livraison"}>
                  <RadioGroup
                    value={form.delivery_mode}
                    onValueChange={(v) => setForm({ ...form, delivery_mode: v })}
                    className="grid grid-cols-2 gap-2"
                  >
                    <label
                      className={`flex items-center gap-2 border px-3 py-3 cursor-pointer transition-colors ${form.delivery_mode === "home" ? "border-neon bg-neon/10" : "border-white/10 hover:border-white/30"}`}
                      data-testid="delivery-mode-home"
                    >
                      <RadioGroupItem value="home" className="border-white/30 text-neon" />
                      <span className="text-sm font-bold">🏠 {lang === "ar" ? "توصيل للمنزل" : "À domicile"}</span>
                      {navexFees && Number(navexFees.tarif_home_dzd) > 0 && !(showDeliveryAmount && deliveryDzd === 0) && (
                        <span className="ms-auto text-neon text-xs font-black">{fmtDzd(homeFeeBase)}</span>
                      )}
                    </label>
                    <label
                      className={`flex items-center gap-2 border px-3 py-3 transition-colors ${
                        !stopDeskAvailable ? "opacity-40 cursor-not-allowed border-white/10" :
                        form.delivery_mode === "stopdesk" ? "border-neon bg-neon/10 cursor-pointer" : "border-white/10 hover:border-white/30 cursor-pointer"
                      }`}
                      data-testid="delivery-mode-stopdesk"
                      title={!stopDeskAvailable ? (lang === "ar" ? "غير متوفر لهذه البلدية" : "Non disponible pour cette commune") : ""}
                    >
                      <RadioGroupItem value="stopdesk" disabled={!stopDeskAvailable} className="border-white/30 text-neon" />
                      <span className="text-sm font-bold">🏢 {lang === "ar" ? "استلام من نقطة" : "Stop Desk"}</span>
                      {navexFees && Number(navexFees.tarif_stopdesk_dzd) > 0 && !(showDeliveryAmount && deliveryDzd === 0) && (
                        <span className="ms-auto text-neon text-xs font-black">{fmtDzd(stopdeskFeeBase)}</span>
                      )}
                    </label>
                  </RadioGroup>
                </Field>
              )}

              {/* Adresse — visible uniquement si Domicile OU pickup magasin (déjà optionnel côté pickup) */}
              {(isPickup || form.delivery_mode === "home" || !form.commune) && (
                <Field label={`${t.form.address}${isPickup ? (lang === "ar" ? " (اختياري)" : " (optionnel)") : ""}`}>
                  <Input
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon"
                    placeholder={isPickup ? (lang === "ar" ? "غير مطلوب — الاستلام في المتجر" : "Non requis — retrait en magasin") : ""}
                    autoComplete="street-address"
                    data-testid="form-address"
                  />
                </Field>
              )}

              {(carriers.length > 1 || (carriers.length > 0 && isPickup)) && (
                <Field label={t.form.delivery}>
                  {isPickup ? (
                    <div className="border border-neon/40 bg-neon/5 p-4" data-testid="pickup-notice">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl leading-none mt-0.5">🏪</span>
                        <div className="flex-1">
                          <div className="font-black uppercase tracking-widest text-neon text-sm mb-1">
                            {lang === "ar" ? "استلام في المتجر" : "Retrait en magasin"}
                          </div>
                          <p className="text-xs text-white/70 leading-relaxed">
                            {lang === "ar"
                              ? "اخترت الدفع والاستلام في المتجر. سنتواصل معك لتأكيد موعد الاستلام. لا توجد رسوم توصيل."
                              : "Vous avez choisi le paiement & retrait en magasin. Nous vous contacterons pour fixer un rendez-vous. Aucun frais de livraison."}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <RadioGroup value={form.delivery} onValueChange={(v) => setForm({ ...form, delivery: v })} className="grid grid-cols-2 gap-2">
                      {carriers.map((c) => (
                        <label key={c.id} className={`flex items-center gap-2 border px-3 py-3 cursor-pointer transition-colors ${form.delivery === c.id ? "border-neon bg-neon/10" : "border-white/10 hover:border-white/30"}`} data-testid={`delivery-${c.id}`}>
                          <RadioGroupItem value={c.id} className="border-white/30 text-neon" />
                          {c.logo_url && <img src={cldUrl(c.logo_url, { w: 120 })} alt={c.name} style={{ height: `${settings.carrier_logos_height_px || 20}px` }} className="w-auto" loading="lazy" decoding="async" />}
                          <span className="text-sm font-bold">{c.name}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  )}
                </Field>
              )}

              {allPaymentOptions.length > 0 && (
                <Field label={t.form.payment}>
                  <RadioGroup value={form.payment} onValueChange={(v) => setForm({ ...form, payment: v })} className="grid grid-cols-2 gap-2">
                    {allPaymentOptions
                      .filter((p) => p._online || !codDisabled || p.id !== "cod")
                      .map((p) => (
                      <label
                        key={p.id}
                        className={`flex items-center gap-2 border px-3 py-3 cursor-pointer transition-colors ${form.payment === p.id ? "border-neon bg-neon/10" : "border-white/10 hover:border-white/30 opacity-90"}`}
                        title={p._online ? (lang === "ar" ? "بطاقة CIB / الذهبية" : "Carte CIB / Edahabia") : ""}
                        data-testid={`payment-${p.id}`}
                      >
                        <RadioGroupItem value={p.id} className="border-white/30 text-neon" />
                        {p.logo_url && <img src={cldUrl(p.logo_url, { w: 120 })} alt={lang === "ar" ? p.label_ar : p.label_fr} style={{ height: `${settings.payment_logos_height_px || 20}px` }} className="w-auto" loading="lazy" decoding="async" />}
                        <span className="text-sm font-bold">{lang === "ar" ? p.label_ar : p.label_fr}</span>
                      </label>
                    ))}
                  </RadioGroup>
                  {codDisabled && (
                    <p className="text-[11px] text-clay mt-2 border border-clay/40 bg-clay/10 p-2" data-testid="cod-disabled-msg">
                      {lang === "ar"
                        ? (settings.cod_threshold_message_ar || `الدفع عند الاستلام غير متاح للطلبات > ${codMaxRaw.toLocaleString("fr-FR")} دج`)
                        : (settings.cod_threshold_message_fr || `Paiement à la livraison indisponible au-delà de ${codMaxRaw.toLocaleString("fr-FR")} DZD.`)}
                    </p>
                  )}
                </Field>
              )}

              {selectedPayment && (selectedPayment.info_fr || selectedPayment.info_ar) && (
                <div className="border border-neon/30 bg-neon/5 p-3 text-xs text-white/80 whitespace-pre-line">
                  <div className="text-neon font-bold uppercase tracking-widest text-[10px] mb-2">{lang === "ar" ? selectedPayment.label_ar : selectedPayment.label_fr}</div>
                  {lang === "ar" ? selectedPayment.info_ar : selectedPayment.info_fr}
                  {selectedPayment.id === "transfer" && settings.rib_info && (<div className="mt-2 pt-2 border-t border-white/10 whitespace-pre-line">{settings.rib_info}</div>)}
                  {selectedPayment.id === "paypal" && settings.paypal_me && (<div className="mt-2"><a href={settings.paypal_me} target="_blank" rel="noreferrer" className="underline break-all hover:text-neon">{settings.paypal_me}</a></div>)}
                </div>
              )}

              <details open={!!form.note || !!form.email}>
                <summary className="text-xs text-white/60 cursor-pointer py-1">{lang === "ar" ? "إضافة بريد إلكتروني أو ملاحظة (اختياري)" : "Ajouter un email ou une note (facultatif)"}</summary>
                <div className="mt-2 space-y-3">
                <Field label={t.form.email || (lang === "ar" ? "البريد الإلكتروني (اختياري)" : "Email (optionnel)")}>
                  <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" inputMode="email" autoComplete="email" placeholder="vous@email.com" className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="form-email" />
                </Field><Field label={t.form.note}><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="form-note" /></Field>
                </div>
              </details>

              <div className="sticky bottom-0 z-10 -mx-6 sm:-mx-8 px-6 sm:px-8 py-3 bg-[var(--c-bg)] border-t border-white/10">
              {!canSubmit && items.length > 0 && missingFields.length > 0 && (
                <p className="text-[11px] text-clay mb-2" data-testid="checkout-missing-fields">
                  {lang === "ar" ? "ناقص : " : "Il manque : "}{missingFields.join(lang === "ar" ? "، " : ", ")}
                </p>
              )}
              <Button
                onClick={() => {
                  const selOption = allPaymentOptions.find((p) => p.id === form.payment);
                  if (selOption?._online) submitOnline(selOption._providerName);
                  else submit();
                }}
                disabled={!canSubmit || !!paying || submitting}
                className="w-full bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest py-6 h-auto disabled:opacity-40"
                data-testid="checkout-whatsapp-btn"
              >
                {(() => {
                  const selOption = allPaymentOptions.find((p) => p.id === form.payment);
                  if (selOption?._online) {
                    return paying
                      ? (lang === "ar" ? "جارٍ التحويل..." : "Redirection…")
                      : (lang === "ar" ? "🔒 ادفع الآن بأمان" : "🔒 Payer maintenant en sécurité");
                  }
                  if (submitting) {
                    return lang === "ar" ? "جارٍ الإرسال..." : "Envoi en cours…";
                  }
                  return <>{t.cart.checkout} · {fmtDzd(finalDzd)}</>;
                })()}
              </Button>
              </div>

              {/* SSL trust badge — appears only when an online payment is selected. */}
              {allPaymentOptions.find((p) => p.id === form.payment)?._online && (
                <div className="flex items-center justify-center gap-2 text-[10px] sm:text-[11px] text-white/60 uppercase tracking-widest font-bold -mt-1" data-testid="ssl-trust-badge">
                  <Lock className="w-3 h-3 text-neon" />
                  <span>{lang === "ar" ? "دفع آمن 100% — SSL مشفر" : "Paiement 100% sécurisé — SSL chiffré"}</span>
                  <span aria-hidden className="text-white/30">•</span>
                  <span className="text-white/50">CIB / Edahabia</span>
                </div>
              )}

              {/* Note: online providers (Chargily / SofizPay / Guiddini) are merged into
                  the main payment radio group above as 'Paiement par Carte CIB / Edahabia',
                  so this dedicated block is intentionally omitted. */}

              <button onClick={clear} className="text-xs text-white/40 hover:text-clay underline underline-offset-4">{lang === "ar" ? "إفراغ السلة" : "Vider le panier"}</button>

              {/* Trust lines (cartes confiance — variante lignes) */}
              <div className="-mx-6 sm:-mx-8 mt-4">
                <Trust variant="lines" />
              </div>

              {/* Reassurance fallback (if Trust disabled and Reassurance enabled) */}
              <div className="-mx-6 sm:-mx-8">
                <ReassuranceBlock variant="lines" compact />
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (<div><Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-2 block">{label}</Label>{children}</div>);
}
